import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

type ToastTone = 'info' | 'error' | 'success';

/**
 * Optionale Handlung im Toast — in der Praxis „Rückgängig".
 *
 * Sie ist der Grund, warum Archivieren ohne Rueckfrage auskommt: eine
 * Bestaetigung unterbricht jeden Vorgang, eine Ruecknahme nur den seltenen
 * Fehlgriff.
 */
export interface ToastAction {
  label: string;
  onAction: () => void;
}

interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
  action?: ToastAction;
}

type PushToast = (message: string, tone?: ToastTone, action?: ToastAction) => void;

const ToastContext = createContext<PushToast | null>(null);

const TONE_CLASS: Record<ToastTone, string> = {
  info: 'bg-surface text-text border-border',
  error: 'bg-danger-soft text-danger border-danger',
  success: 'bg-success-soft text-success border-success',
};

/** Ohne Handlung reicht ein kurzer Hinweis; mit Handlung braucht es Zeit zum Zielen. */
const PLAIN_MS = 4000;
const WITH_ACTION_MS = 8000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback<PushToast>(
    (message, tone = 'info', action) => {
      const id = Date.now() + Math.random();
      setToasts((current) => [...current, { id, message, tone, ...(action ? { action } : {}) }]);
      const timer = setTimeout(
        () => dismiss(id),
        action ? WITH_ACTION_MS : PLAIN_MS,
      );
      timers.current.add(timer);
    },
    [dismiss],
  );

  // Beim Verlassen keine Timer stehen lassen, die in eine abgeraeumte
  // Komponente schreiben.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const value = useMemo(() => push, [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed inset-x-0 bottom-20 z-50 flex flex-col items-center gap-2 px-4"
        role="status"
        aria-live="polite"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            data-testid="toast"
            className={`pointer-events-auto flex w-full max-w-sm items-center justify-between gap-3 rounded-[var(--radius)] border px-4 py-3 text-sm shadow-[var(--shadow-2)] ${TONE_CLASS[toast.tone]}`}
          >
            <span className="min-w-0">{toast.message}</span>
            {toast.action && (
              <button
                type="button"
                data-testid="toast-action"
                className="shrink-0 rounded-[var(--radius-sm)] border border-current px-3 py-1 font-semibold"
                onClick={() => {
                  dismiss(toast.id);
                  toast.action?.onAction();
                }}
              >
                {toast.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): PushToast {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast benoetigt einen ToastProvider');
  return context;
}
